import { create } from "zustand";

export type ViewMode = "sections" | "theme-settings";
export type ThemeTab =
  | "colors"
  | "typography"
  | "appearance"
  | "searchBar"
  | "nav"
  | "animations"
  | "productCard"
  | "labels"
  | "shopTab"
  | "checkout"
  | "orders"
  | "restock"
  | "contactUs"
  | "appGate"
  | "advanced";

export interface Selection {
  sectionId: string | null;
  blockId: string | null;
}

interface SelectionState {
  viewMode: ViewMode;
  themeTab: ThemeTab;
  /** Active page-template (e.g. "index", "product", "collection.adidas") */
  template: string;
  selection: Selection;
  /** sectionIds whose block list is currently expanded in the tree */
  expanded: Record<string, boolean>;

  setViewMode: (mode: ViewMode) => void;
  setThemeTab: (tab: ThemeTab) => void;
  setTemplate: (template: string) => void;
  selectSection: (sectionId: string) => void;
  selectBlock: (sectionId: string, blockId: string) => void;
  clearSelection: () => void;
  toggleExpanded: (sectionId: string) => void;
}

export const useSelectionStore = create<SelectionState>((set) => ({
  viewMode: "sections",
  themeTab: "colors",
  template: "index",
  selection: { sectionId: null, blockId: null },
  expanded: {},

  setViewMode: (mode) => set({ viewMode: mode }),
  setThemeTab: (tab) => set({ themeTab: tab }),
  setTemplate: (template) =>
    // Switching template invalidates the section/block selection (the new
    // template has different sections), but the chosen view mode survives.
    set({
      template,
      selection: { sectionId: null, blockId: null },
      expanded: {},
    }),
  selectSection: (sectionId) =>
    set((s) => ({
      selection: { sectionId, blockId: null },
      expanded: { ...s.expanded, [sectionId]: true },
    })),
  selectBlock: (sectionId, blockId) =>
    set((s) => ({
      selection: { sectionId, blockId },
      expanded: { ...s.expanded, [sectionId]: true },
    })),
  clearSelection: () => set({ selection: { sectionId: null, blockId: null } }),
  toggleExpanded: (sectionId) =>
    set((s) => ({
      expanded: { ...s.expanded, [sectionId]: !s.expanded[sectionId] },
    })),
}));
